import { useEffect, useMemo, useState, useCallback } from "react";
import { useQuery } from "convex/react";
import { api } from "@/convex/src/_generated/api";
import { ServersTab } from "./components/ServersTab";
import { ToolsTab } from "./components/ToolsTab";
import { WidgetsTab } from "./components/WidgetsTab";
import { ResourcesTab } from "./components/ResourcesTab";
import { PromptsTab } from "./components/PromptsTab";
import { ChatTab } from "./components/ChatTab";
import { ChatTabV2 } from "./components/ChatTabV2";
import { EvalsTab } from "./components/EvalsTab";
import { SettingsTab } from "./components/SettingsTab";
import { TracingTab } from "./components/TracingTab";
import { InterceptorTab } from "./components/InterceptorTab";
import { AuthTab } from "./components/AuthTab";
import { OAuthFlowTab } from "./components/OAuthFlowTab";
import { RegistryTab } from "./components/RegistryTab";
import { FlowsTab } from "./components/flows/FlowsTab";
import OAuthDebugCallback from "./components/oauth/OAuthDebugCallback";
import { DocsPage } from "./components/docs/DocsPage";
import { BlogListPage } from "./components/blog/BlogListPage";
import { BlogPostPage } from "./components/blog/BlogPostPage";
import { MCPSidebar } from "./components/mcp-sidebar";
import { WidgetPreviewPlayground } from "./components/widgets/WidgetPreviewPlayground";
import { ActiveServerSelector } from "./components/ActiveServerSelector";
import { HomePage } from "./components/HomePage";
import { LandingPage } from "./components/LandingPage";
import {
  SidebarInset,
  SidebarProvider,
  useSidebar,
} from "./components/ui/sidebar";
import { useAppState } from "./hooks/use-app-state";
import { PreferencesStoreProvider } from "./stores/preferences/preferences-provider";
import { RegistryStoreProvider } from "./stores/registry/registry-provider";
import { Toaster } from "./components/ui/sonner";
import { useElectronOAuth } from "./hooks/useElectronOAuth";
import { useEnsureDbUser } from "./hooks/useEnsureDbUser";
import { usePostHog } from "posthog-js/react";
import { usePostHogIdentify } from "./hooks/usePostHogIdentify";

// Import global styles
import "./index.css";
import { detectEnvironment, detectPlatform } from "./logs/PosthogUtils";
import {
  getInitialThemeMode,
  updateThemeMode,
  getInitialThemePreset,
  updateThemePreset,
} from "./lib/theme-utils";
import { CallbackPage } from "./components/CallbackPage";
import LoadingScreen from "./components/LoadingScreen";
import { useLoginPage } from "./hooks/use-log-in-page";
import { Header } from "./components/Header";
import { ThemePreset } from "./types/preferences/theme";
import {
  AppWorkspaceProvider,
  useAppWorkspace,
} from "./contexts/AppWorkspaceContext";
import { OnboardingDialog } from "./components/app-workspace/OnboardingDialog";
import { useInvitationHandler } from "./hooks/useInvitationHandler";
import { InvitationErrorDialog } from "./components/app-workspace/InvitationErrorDialog";
import { toast } from "sonner";
import { migrateLocalStorage } from "./lib/storage-migration";
import { hasAuthCookie } from "./lib/auth-utils";
import { Router, Route, Switch, useParams } from "wouter";

// Wrapper component that handles sidebar auto-collapse for settings
function SidebarAutoCollapseWrapper({
  activeTab,
  prevTab,
  setPrevTab,
  sidebarStateBeforeSettings,
  setSidebarStateBeforeSettings,
  children,
}: {
  activeTab: string;
  prevTab: string;
  setPrevTab: (tab: string) => void;
  sidebarStateBeforeSettings: boolean | null;
  setSidebarStateBeforeSettings: (state: boolean | null) => void;
  children: React.ReactNode;
}) {
  const { open, setOpen } = useSidebar();

  useEffect(() => {
    const isEnteringSettings =
      activeTab === "settings" && prevTab !== "settings";
    const isLeavingSettings =
      activeTab !== "settings" && prevTab === "settings";

    if (isEnteringSettings) {
      // Entering settings - save current state and collapse
      setSidebarStateBeforeSettings(open);
      setOpen(false);
      // Update prevTab immediately to prevent re-triggering on toggle
      setPrevTab("settings");
    } else if (isLeavingSettings) {
      // Leaving settings - restore previous state
      if (sidebarStateBeforeSettings !== null) {
        setOpen(sidebarStateBeforeSettings);
        setSidebarStateBeforeSettings(null);
      }
    }
  }, [
    activeTab,
    prevTab,
    open,
    setOpen,
    sidebarStateBeforeSettings,
    setSidebarStateBeforeSettings,
    setPrevTab,
  ]);

  return <>{children}</>;
}

// Inner component that has access to AppWorkspace context
function AppContent() {
  // Run storage migration once on mount to clean up old workspace data
  useEffect(() => {
    migrateLocalStorage();
  }, []);

  // Helper to parse hash with potential sub-routes (e.g., #settings/mcp-server)
  const parseHash = (hash: string) => {
    const cleanHash = hash.replace("#", "");
    const [mainTab, subRoute] = cleanHash.split("/");
    return { mainTab: mainTab || "app", subRoute: subRoute || undefined };
  };

  const [activeTab, setActiveTab] = useState(() => {
    const { mainTab } = parseHash(window.location.hash);
    return mainTab;
  });
  const [settingsSection, setSettingsSection] = useState<string | undefined>(
    () => {
      const { mainTab, subRoute } = parseHash(window.location.hash);
      return mainTab === "settings" ? subRoute : undefined;
    },
  );
  const [prevTab, setPrevTab] = useState(() => {
    const { mainTab } = parseHash(window.location.hash);
    return mainTab;
  });
  const [sidebarStateBeforeSettings, setSidebarStateBeforeSettings] = useState<
    boolean | null
  >(null);

  const posthog = usePostHog();
  const { shouldShowLoginPage, isAuthenticated, isAuthLoading } =
    useLoginPage();

  usePostHogIdentify();

  // Track app launch
  useEffect(() => {
    if (isAuthLoading) return;
    posthog.capture("app_launched", {
      platform: detectPlatform(),
      environment: detectEnvironment(),
      user_agent: navigator.userAgent,
      version: __APP_VERSION__,
      is_authenticated: isAuthenticated,
    });
  }, [isAuthLoading, isAuthenticated, posthog]);

  // Track pageviews on tab/route changes
  useEffect(() => {
    const pathname = window.location.pathname;
    const pageName = pathname.startsWith("/docs")
      ? "docs"
      : pathname === "/callback"
        ? "callback"
        : activeTab;

    posthog.capture("$pageview", {
      $current_url: window.location.href,
      page_name: pageName,
      is_authenticated: isAuthenticated,
    });

    // Capture $pageleave when the tab changes to track engagement metrics
    // (time on page, scroll depth, etc.)
    return () => {
      posthog.capture("$pageleave", undefined, { transport: "sendBeacon" });
    };
  }, [activeTab, posthog, isAuthenticated]);

  // Redirect authenticated users from / to /#app for explicit URL
  useEffect(() => {
    if (
      isAuthenticated &&
      window.location.pathname === "/" &&
      !window.location.hash
    ) {
      window.history.replaceState(null, "", "/#app");
    }
  }, [isAuthenticated]);

  // Set up Electron OAuth callback handling
  useElectronOAuth();
  // Ensure a `users` row exists after Convex auth
  useEnsureDbUser();

  // App workspace onboarding logic - MUST BE AT TOP LEVEL!
  const {
    workspaces: appWorkspaces,
    createWorkspace,
    isLoadingWorkspaces,
    activeWorkspace,
  } = useAppWorkspace();
  const invitationHandler = useInvitationHandler();

  // Query servers to find the primary server name
  const workspaceServers = useQuery(
    api.appWorkspaceServers.listServers,
    activeWorkspace?._id ? { workspaceId: activeWorkspace._id } : "skip",
  );

  // Get the primary server name from the workspace
  const primaryServerName = useMemo(() => {
    if (!activeWorkspace?.primaryServerId || !workspaceServers) return null;
    const primaryServer = workspaceServers.find(
      (s) => s._id === activeWorkspace.primaryServerId,
    );
    return primaryServer?.name ?? null;
  }, [activeWorkspace?.primaryServerId, workspaceServers]);

  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [onboardingMode, setOnboardingMode] = useState<"create" | "invited">(
    "create",
  );
  const [invitedWorkspaceName, setInvitedWorkspaceName] = useState<string>();
  const [hasCheckedInvitation, setHasCheckedInvitation] = useState(false);
  const [showInvitationError, setShowInvitationError] = useState(false);

  // Persist onboarding completion to localStorage to prevent it from showing repeatedly
  const ONBOARDING_COMPLETED_KEY = "app-onboarding-completed";

  const getHasCompletedOnboarding = useCallback(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(ONBOARDING_COMPLETED_KEY) === "true";
  }, []);

  const markOnboardingCompleted = useCallback(() => {
    localStorage.setItem(ONBOARDING_COMPLETED_KEY, "true");
  }, []);

  const isDebugCallback = useMemo(
    () => window.location.pathname.startsWith("/oauth/callback/debug"),
    [],
  );
  const isOAuthCallback = useMemo(
    () => window.location.pathname === "/callback",
    [],
  );
  const isOAuthCallbackComplete = useMemo(
    () => window.location.pathname.startsWith("/oauth/callback"),
    [],
  );
  // Note: Public pages (docs, blog, landing) are now handled at the App level
  // before AppContent is rendered, so they render immediately without auth checks.

  const {
    appState,
    isLoading,
    connectedServerConfigs,
    selectedMCPConfig,
    handleConnect,
    handleDisconnect,
    handleReconnect,
    handleUpdate,
    handleRemoveServer,
    setSelectedServer,
    toggleServerSelection,
    selectedMCPConfigsMap,
    setSelectedMultipleServersToAllServers,
  } = useAppState();
  // Sync tab with hash on mount and when hash changes
  useEffect(() => {
    const applyHash = () => {
      const { mainTab, subRoute } = parseHash(window.location.hash);
      setActiveTab(mainTab);

      // Update settings section if navigating to settings
      if (mainTab === "settings") {
        setSettingsSection(subRoute);
      }

      if (mainTab === "chat" || mainTab === "chat-v2") {
        setSelectedMultipleServersToAllServers();
      }
      // Auto-select primary server for certain pages
      if (
        pagesWithPrimaryServerAutoSelect.includes(mainTab) &&
        primaryServerName
      ) {
        setSelectedServer(primaryServerName);
      }
    };
    applyHash();
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
  }, [
    setSelectedMultipleServersToAllServers,
    primaryServerName,
    setSelectedServer,
  ]);

  // Pages that should auto-select the primary server
  const pagesWithPrimaryServerAutoSelect = [
    "widgets",
    "tools",
    "flows",
    "resources",
    "prompts",
  ];

  const handleNavigate = useCallback(
    (section: string) => {
      const { mainTab, subRoute } = parseHash(section);

      if (mainTab === "chat" || mainTab === "chat-v2") {
        setSelectedMultipleServersToAllServers();
      }
      // Auto-select primary server for certain pages
      if (
        pagesWithPrimaryServerAutoSelect.includes(mainTab) &&
        primaryServerName
      ) {
        setSelectedServer(primaryServerName);
      }
      // Update settings section if navigating to settings
      if (mainTab === "settings") {
        setSettingsSection(subRoute);
      }
      setPrevTab(activeTab);
      window.location.hash = section;
      setActiveTab(mainTab);
    },
    [
      setSelectedMultipleServersToAllServers,
      primaryServerName,
      setSelectedServer,
      activeTab,
    ],
  );

  // Check for invitation token in URL and store it (before authentication)
  useEffect(() => {
    const hasToken = invitationHandler.checkAndStoreUrlToken();
    if (hasToken) {
      // Token stored, will be processed after authentication
      console.log(
        "[Invitation] Token stored from URL, will process after auth",
      );
    }
  }, []); // Run once on mount

  // Process invitation after authentication
  useEffect(() => {
    if (!isAuthenticated || isLoadingWorkspaces || hasCheckedInvitation) return;

    const processInvitation = async () => {
      // Try to get token from URL first (for already authenticated users)
      const urlParams = new URLSearchParams(window.location.search);
      const urlToken = urlParams.get("invite");

      // Or get from storage (after SSO redirect)
      const storedToken = invitationHandler.getStoredToken();
      const token = urlToken || storedToken;

      if (token) {
        try {
          // Accept invitation - returns structured result
          const result = await invitationHandler.acceptInvitation(token);

          if (result) {
            const workspace = appWorkspaces.find(
              (w) => w._id === result.workspaceId,
            );
            const workspaceName = workspace?.name || "the workspace";

            // Show appropriate toast based on action taken
            switch (result.action) {
              case "joined":
                toast.success(`Welcome to ${workspaceName}!`);
                setInvitedWorkspaceName(workspaceName);
                setOnboardingMode("invited");
                setOnboardingOpen(true);
                break;

              case "upgraded":
                toast.success(
                  `Your role in ${workspaceName} has been upgraded from ${result.oldRole} to ${result.newRole}!`,
                );
                break;

              case "already_member":
                toast.info(`You're already a member of ${workspaceName}`);
                break;

              case "already_member_higher_role":
                toast.info(
                  `You're already a ${result.currentRole} in ${workspaceName}`,
                );
                break;
            }

            // Clear URL parameter
            invitationHandler.clearUrlToken();
            // Mark onboarding as completed when joining via invitation
            markOnboardingCompleted();
          }
        } catch (error: any) {
          console.error("Failed to accept invitation:", error);
          // Show error dialog for structured errors
          setShowInvitationError(true);

          // Still show onboarding if user has no workspaces AND hasn't completed onboarding before
          if (appWorkspaces.length === 0 && !getHasCompletedOnboarding()) {
            setOnboardingMode("create");
            setOnboardingOpen(true);
          }
        }
      } else if (
        appWorkspaces.length === 0 &&
        !isLoadingWorkspaces &&
        !getHasCompletedOnboarding()
      ) {
        // No invitation, no workspaces, and hasn't completed onboarding - show create flow
        setOnboardingMode("create");
        setOnboardingOpen(true);
      }

      setHasCheckedInvitation(true);
    };

    processInvitation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthenticated,
    isLoadingWorkspaces,
    hasCheckedInvitation,
    markOnboardingCompleted,
    getHasCompletedOnboarding,
  ]);

  const handleOnboardingCreate = async (name: string) => {
    await createWorkspace(name);
    markOnboardingCompleted();
    setOnboardingOpen(false);
  };

  // Compute whether to show onboarding
  const showOnboarding = isAuthenticated && onboardingOpen;

  if (isDebugCallback) {
    return <OAuthDebugCallback />;
  }

  // /callback route - handle OAuth callback with CallbackPage component
  if (isOAuthCallback) {
    return <CallbackPage />;
  }

  // Wait for both auth and app state to settle before rendering
  // This prevents flash of authenticated nav for unauthenticated users
  if (isAuthLoading || isLoading) {
    return <LoadingScreen />;
  }

  const appContent = (
    <SidebarProvider>
      <SidebarAutoCollapseWrapper
        activeTab={activeTab}
        prevTab={prevTab}
        setPrevTab={setPrevTab}
        sidebarStateBeforeSettings={sidebarStateBeforeSettings}
        setSidebarStateBeforeSettings={setSidebarStateBeforeSettings}
      >
        <MCPSidebar onNavigate={handleNavigate} activeTab={activeTab} />
        <SidebarInset className="flex flex-col min-h-0">
          <Header
            connectedServerConfigs={connectedServerConfigs}
            onReconnect={handleReconnect}
          />
          <div className="flex flex-1 min-h-0 flex-col overflow-hidden h-full">
            {/* Active Server Selector - Hidden but kept for functionality */}
            {/* To show again, remove the hidden class from the wrapper div below */}
            <div className="hidden">
              {(activeTab === "widgets" ||
                activeTab === "tools" ||
                activeTab === "flows" ||
                activeTab === "resources" ||
                activeTab === "prompts" ||
                activeTab === "auth" ||
                activeTab === "oauth-flow" ||
                activeTab === "chat" ||
                activeTab === "chat-v2" ||
                activeTab === "interceptor") && (
                <ActiveServerSelector
                  connectedServerConfigs={connectedServerConfigs}
                  selectedServer={appState.selectedServer}
                  onServerChange={setSelectedServer}
                  onConnect={handleConnect}
                  isMultiSelectEnabled={
                    activeTab === "chat" || activeTab === "chat-v2"
                  }
                  onMultiServerToggle={toggleServerSelection}
                  selectedMultipleServers={appState.selectedMultipleServers}
                  showOnlyOAuthServers={activeTab === "oauth-flow"}
                />
              )}
            </div>

            {/* Content Areas */}
            {activeTab === "app" &&
              (isAuthenticated ? <HomePage /> : <LandingPage />)}

            {activeTab === "servers" && (
              <ServersTab
                connectedServerConfigs={connectedServerConfigs}
                onConnect={handleConnect}
                onDisconnect={handleDisconnect}
                onReconnect={handleReconnect}
                onUpdate={handleUpdate}
                onRemove={handleRemoveServer}
              />
            )}

            {activeTab === "registry" && (
              <RegistryTab onConnect={handleConnect} />
            )}

            {activeTab === "widgets" && (
              <div className="h-full overflow-hidden">
                <WidgetsTab
                  serverConfig={selectedMCPConfig}
                  serverName={appState.selectedServer}
                />
              </div>
            )}

            {activeTab === "tools" && (
              <div className="h-full overflow-hidden">
                <ToolsTab
                  serverConfig={selectedMCPConfig}
                  serverName={appState.selectedServer}
                />
              </div>
            )}

            {activeTab === "flows" && (
              <div className="h-full overflow-hidden">
                <FlowsTab
                  serverConfig={selectedMCPConfig}
                  serverName={appState.selectedServer}
                />
              </div>
            )}

            {activeTab === "evals" && <EvalsTab />}
            {activeTab === "resources" && (
              <ResourcesTab
                serverConfig={selectedMCPConfig}
                serverName={appState.selectedServer}
              />
            )}

            {activeTab === "prompts" && (
              <PromptsTab
                serverConfig={selectedMCPConfig}
                serverName={appState.selectedServer}
              />
            )}

            {activeTab === "auth" && (
              <AuthTab
                serverConfig={selectedMCPConfig}
                serverEntry={appState.servers[appState.selectedServer]}
                serverName={appState.selectedServer}
              />
            )}

            {activeTab === "oauth-flow" && (
              <OAuthFlowTab
                serverConfig={selectedMCPConfig}
                serverEntry={appState.servers[appState.selectedServer]}
                serverName={appState.selectedServer}
                onUpdate={handleUpdate}
              />
            )}

            {activeTab === "chat" && (
              <ChatTab
                serverConfigs={selectedMCPConfigsMap}
                connectedServerConfigs={connectedServerConfigs}
              />
            )}

            {activeTab === "chat-v2" && (
              <ChatTabV2
                connectedServerConfigs={connectedServerConfigs}
                selectedServerNames={appState.selectedMultipleServers}
              />
            )}

            {activeTab === "interceptor" && (
              <InterceptorTab
                connectedServerConfigs={connectedServerConfigs}
                selectedServer={appState.selectedServer}
              />
            )}

            {activeTab === "tracing" && <TracingTab />}

            {activeTab === "settings" && (
              <SettingsTab
                connectedServerConfigs={connectedServerConfigs}
                onConnect={handleConnect}
                onDisconnect={handleDisconnect}
                onReconnect={handleReconnect}
                onUpdate={handleUpdate}
                onRemove={handleRemoveServer}
                initialSection={settingsSection}
              />
            )}

            {activeTab === "widget-preview" && <WidgetPreviewPlayground />}
          </div>
        </SidebarInset>
      </SidebarAutoCollapseWrapper>
    </SidebarProvider>
  );

  return (
    <>
      {shouldShowLoginPage && !isOAuthCallbackComplete ? (
        <LandingPage />
      ) : showOnboarding ? (
        // Show full-screen onboarding only, blocking app content
        <OnboardingDialog
          open={onboardingOpen}
          mode={onboardingMode}
          invitedWorkspaceName={invitedWorkspaceName}
          onCreateWorkspace={handleOnboardingCreate}
          onClose={() => setOnboardingOpen(false)}
        />
      ) : (
        appContent
      )}

      {/* Invitation Error Dialog */}
      <InvitationErrorDialog
        open={showInvitationError}
        error={invitationHandler.error}
        onClose={() => setShowInvitationError(false)}
        onSwitchAccount={() => {
          // Handle logout/switch account
          window.location.href = "/api/auth/logout";
        }}
        onRequestNewInvite={() => {
          // Close dialog and show a message
          setShowInvitationError(false);
          toast.info(
            "Please contact your workspace administrator for a new invitation",
          );
        }}
      />
    </>
  );
}

// Helper to detect if we're on a public marketing route
// These routes use client-side routing and skip heavy auth/workspace providers
function isPublicRoute(): boolean {
  const pathname = window.location.pathname;

  // /app route: redirect unauthenticated users to / for proper routing
  // This prevents them from getting stuck in AppContent with broken navigation
  if (pathname === "/app" && !hasAuthCookie()) {
    window.location.replace("/");
    return true; // Return true to prevent rendering while redirect happens
  }

  // If user has auth cookie at root, go to authenticated app
  // (AppContent will handle redirect to /#app)
  if (pathname === "/" && hasAuthCookie()) {
    return false;
  }

  // Docs page - always public
  if (pathname.startsWith("/docs")) return true;

  // Blog routes - always public
  if (pathname.startsWith("/blog")) return true;

  // Landing page for unauthenticated users
  if (pathname === "/" && !window.location.hash) return true;

  return false;
}

// Wrapper component for /blog/:slug routes
// Determines whether to show BlogListPage (for categories) or BlogPostPage (for posts)
import { getAllCategories } from "./components/blog/blog-content";

function BlogSlugRouter() {
  const params = useParams<{ slug: string }>();
  const slug = params.slug?.toLowerCase();

  // Get all category slugs
  const categories = getAllCategories();
  const categorySlugSet = new Set(
    categories.map((cat) => cat.toLowerCase().replace(/\s+/g, "-")),
  );

  // If the slug matches a category, show the blog list filtered by category
  if (slug && categorySlugSet.has(slug)) {
    return <BlogListPage />;
  }

  // Otherwise, show the blog post
  return <BlogPostPage />;
}

// Router component for public marketing pages
// Uses Wouter for instant client-side navigation
function PublicRouter() {
  return (
    <Router>
      <Switch>
        <Route path="/" component={LandingPage} />
        <Route path="/docs" component={DocsPage} />
        <Route path="/blog" component={BlogListPage} />
        <Route path="/blog/:slug" component={BlogSlugRouter} />
      </Switch>
    </Router>
  );
}

// Main App component that provides all contexts
export default function App() {
  const initialThemeMode = getInitialThemeMode();
  const initialThemePreset: ThemePreset = getInitialThemePreset();

  // Set the initial theme mode and preset on page load
  useEffect(() => {
    updateThemeMode(initialThemeMode);
    updateThemePreset(initialThemePreset);
  }, [initialThemeMode, initialThemePreset]);

  // Check for public routes FIRST - render immediately without heavy providers
  // Public pages use Wouter for instant client-side navigation
  if (isPublicRoute()) {
    return (
      <PreferencesStoreProvider
        themeMode={initialThemeMode}
        themePreset={initialThemePreset}
      >
        <Toaster />
        <PublicRouter />
      </PreferencesStoreProvider>
    );
  }

  // Authenticated app routes get full provider stack
  return (
    <PreferencesStoreProvider
      themeMode={initialThemeMode}
      themePreset={initialThemePreset}
    >
      <RegistryStoreProvider>
        <Toaster />
        <AppWorkspaceProvider>
          <AppContent />
        </AppWorkspaceProvider>
      </RegistryStoreProvider>
    </PreferencesStoreProvider>
  );
}
